import dayjs, { Dayjs } from "utils/dayjs"

import { Guid, IApiLink, ITranslations } from "@/types"
import { ApiLinkEntity, ApiLinkRel, Purpose } from "config/constants"
import { toDateTimeWithoutTimezone, toDateWithoutTimezone } from "utils/utils"
import AmountWithCurrency, { IAmountWithCurrencyDto } from "./AmountWithCurrency"
import { AssetCategory, IGetAssetCategoryDto } from "./AssetCategory"
import { AssetType, IGetAssetTypeDto } from "./AssetType"
import Customer, { ICustomerDto } from "./Customer"
import AssetProduct, { IGetAssetProductDto } from "./AssetProduct"
import { prepareTranslations } from "utils/translations"
import MonthlyRentalCost, { IMonthlyRentalCostDto } from "./MonthlyRentalCost"
import AssetLocation, { IGetAssetLocationDto, IPostAssetLocationDto } from "./AssetLocation"
import i18n from "intl/i18n"


export enum AssetStatus {
    ordered = "ordered",
    confirmed = "confirmed",
    available = "available",
    active = "active",
    inactive = "inactive",
}

export enum AssetAvailability {
    inactive = "inactive",
    notAvailable = "notAvailable",
    available = "available",
    planned = "planned",
    salePlanned = "salePlanned",
    inUse = "inUse",
    borrowedExpired = "borrowedExpired"
}

export enum AssetDisposalReason {
    scrap = "scrap",
    stolen = "stolen",
    lost = "lost",
    inventoryAdjustment = "inventoryAdjustment",
    sold = "sold",
    canceled = "canceled",
    endOfHoldership = "endOfHoldership",
    transit = "transit",
    transfer = "transfer",
    reversal = "reversal",
}

export enum AssetOrigin {
    inHouseProduction = "inHouseProduction",
    isTrade = "isTrade",
}

export enum AssetOwnershipType {
    purchased = "purchased",
    borrowed = "borrowed",
}

export enum AssetReferenceNumberType {
    Initial = "Initial",
    Current = "Current"
}

interface IAssetDto {
    ownershipType: AssetOwnershipType
    articleId: Guid
    constructionYear: number
    isSecondhand: boolean
    origin: AssetOrigin | null
    internalOrderNumber?: string | null
    supplierOrderNo?: string | null
    isOperational: boolean
    purposes: Purpose[]
    drawingNo?: string | null
    inventoryNo?: string | null
    internalNotes?: string | null
    serialNumber?: string | null
    serialNumberMachine?: string | null
    operationHours?: number | null
    operationHoursTime?: string | null
    width?: number | null
    height?: number | null
    length?: number | null
    weight?: number | null
    volume?: number | null
    quickCouplingSystem?: string | null
    connectionType?: string | null
    teeth?: boolean | null
    cuttingEdge?: boolean | null
    heavyDuty?: boolean | null
    locking?: string | null
    controlSystem?: string | null
    agility?: string | null
    measurementSystemId?: Guid | null
    emissionLevel?: string | null
    availabilityDate?: string | null
    expectedReturnDate?: string | null
    buyBack?: IAssetBuyBack | null
    availability?: AssetAvailability | null
    plannedUntilOrUnavailableFromDate?: string | null
    availabilityReservationNumber?: string | null
    availabilityReservationId?: Guid | null
    warehouseId?: Guid | null
    profitCenterId?: Guid | null
    disposal?: {
        reason: AssetDisposalReason
        date: string
        description?: string | null
    } | null
    owner?: ICustomerDto | null
    colorId?: Guid | null
    assetStorage?: {
        title?: string | null
        description?: string | null
    } | null
}

export interface IAssetFinancialDto {
    referenceNumberSale: string
    referenceNumberRental?: string | null
    referenceValue?: IAmountWithCurrencyDto
    referenceValueCorrection?: number | null
    monthlyRentalCost?: IMonthlyRentalCostDto
    initialReferenceNumberSale?: string | null
    initialReferenceNumberRental?: string | null
}

export interface IGetAssetFinancialDto extends IAssetFinancialDto {
    assetCategory?: IGetAssetCategoryDto
    assetType?: IGetAssetTypeDto
    assetProduct?: IGetAssetProductDto
    monthlyRentalCost?: IMonthlyRentalCostDto
}

export interface IPostAssetFinancialDto extends IAssetFinancialDto {
    assetCategoryId?: Guid
    assetTypeId?: Guid
    assetProductId?: Guid
}

export interface IGetAssetListPriceDto {
    listPrice?: number | null
    listPriceDate?: string | null
    discount?: number | null
    discountAmount?: number | null
    salesSupport?: number | null
    salesPrice?: number | null
    salesPriceAdjustedCondition?: number | null
}

export interface IFreightUnitBase {
    measurementSystemId?: Guid | null
    dimension1?: number | null
    dimension2?: number | null
    dimension3?: number | null
    weight?: number | null
    volume?: number | null
    translations: ITranslations
}

export interface IGetFreightUnit extends IFreightUnitBase {
    description: string
}

export interface IPostFreightUnit extends IFreightUnitBase {

}

export interface IPostAssetRequestStatisticDTO {
    relevantForAssetStatistic: boolean
    reportAsset: boolean
    reservationId?: Guid | null
}

export interface IGetAssetRequestStatisticDTO extends IPostAssetRequestStatisticDTO {
    changeUserId?: Guid | null
    changeDate?: Guid | null
    locked: boolean
}

export interface IGetAssetRequestStatistic extends Omit<IGetAssetRequestStatisticDTO, "changeDate"> {
    changeDate?: Dayjs | null
}

export interface IAssetBuyBack {
    customerName: string
    customerNumber?: string | null
    salesPersonNumber?: string | null
    reference?: string | null
}

export interface IPostAssetDto extends IAssetDto {
    lmid?: string | null
    assetLocation: IPostAssetLocationDto
    condition?: number
    assetFinancial?: IPostAssetFinancialDto
    assetListPrice?: IGetAssetListPriceDto
    manufacturerCode: string
    features?: ITranslations | null
    freightUnits?: IPostFreightUnit[] | null
    assetRequestStatistic?: IPostAssetRequestStatisticDTO | null
    status?: AssetStatus
}

export interface IGetAssetDto extends IAssetDto {
    id: Guid
    lmid: string
    altArticleDescription?: string | null
    holderName?: string | null
    isLabeled: boolean
    labels?: string[] | null
    assetLocation: IGetAssetLocationDto
    condition?: number | null
    linkedToAssetCollection: boolean
    version: number
    isCurrentVersion: boolean
    owner?: ICustomerDto | null
    manufacturerCode?: string | null
    features?: ITranslations | null
    freightUnits?: IGetFreightUnit[] | null
    splitParentId?: Guid | null
    splitParentLmid?: string | null
    splitDate?: string | null
    assetSplitIsRunning: boolean
    profitCenterDescription?: string | null
    assetRequestStatistic?: IGetAssetRequestStatisticDTO | null
    lastScanDate?: string | null
    created?: string
    createdBy?: Guid
    changed?: string
    changedBy?: Guid
    status: AssetStatus
    wasReleased: boolean
    links: IApiLink[]
    eTag: string
}

export interface IAsset {
    id?: Guid
    articleId: Guid
    articleDescription?: string
    altArticleDescription?: string
    ownershipType: AssetOwnershipType
    lmid: string
    constructionYear: number
    isSecondhand: boolean
    origin: AssetOrigin | null
    internalOrderNumber?: string | null
    supplierOrderNo?: string | null
    isOperational: boolean
    condition?: number
    holderName?: string
    purposes: Purpose[]
    drawingNo?: string | null
    inventoryNo?: string | null
    internalNotes?: string | null
    serialNo?: string | null
    serialNoMachine?: string | null
    manufacturerCode?: string | null
    operationHours?: number | null
    operationHoursTime?: Dayjs | null
    width?: number | null
    height?: number | null
    length?: number | null
    weight?: number | null
    volume?: number | null
    quickCouplingSystem?: string | null
    connectionType?: string | null
    teeth?: boolean | null
    cuttingEdge?: boolean | null
    heavyDuty?: boolean | null
    locking?: string | null
    controlSystem?: string | null
    agility?: string | null
    measurementSystemId?: Guid | null
    emissionLevel?: string | null
    availabilityDate?: Dayjs | null
    expectedReturnDate?: Dayjs | null
    availability?: AssetAvailability | null
    plannedUntilOrUnavailableFromDate?: Dayjs | null
    availabilityReservationNumber?: string | null
    availabilityReservationId?: Guid | null
    features?: ITranslations | null
    freightUnits: IGetFreightUnit[]
    splitParentId?: Guid | null
    splitParentLmid?: string | null
    splitDate?: Dayjs | null
    assetSplitIsRunning?: boolean
    isLabeled?: boolean
    labels?: string[] | null
    assetLocation: AssetLocation
    buyBack?: IAssetBuyBack | null
    warehouseId?: Guid
    profitCenterId?: Guid
    profitCenterDescription?: string
    lastUpdateDate?: Dayjs
    assetFinancial: {
        referenceNumberSale: string
        referenceNumberRental?: string | null
        referenceValue?: AmountWithCurrency
        referenceValueCorrection?: number | null
        assetCategory?: AssetCategory
        assetType?: AssetType
        assetProduct?: AssetProduct
        monthlyRentalCost?: MonthlyRentalCost
        initialReferenceNumberSale?: string | null
        initialReferenceNumberRental?: string | null
    }
    disposal?: {
        reason: AssetDisposalReason
        date: Dayjs
        description?: string
        disposedByEmail?: string
    }
    linkedToAssetCollection?: boolean
    version?: number
    isCurrentVersion?: boolean
    owner?: Customer | null
    colorId?: Guid
    assetListPrice?: {
        listPrice?: number | null
        listPriceDate?: Dayjs | null
        discount?: number | null
        discountAmount?: number | null,
        salesSupport?: number | null
        salesPrice?: number | null
        salesPriceAdjustedCondition?: number | null
    } | null
    assetStorage?: {
        title?: string
        description?: string
    } | null
    status: AssetStatus
    wasReleased?: boolean
    assetRequestStatistic?: IGetAssetRequestStatistic | null
    lastScanDate?: Dayjs
    created?: Dayjs
    createdBy?: Guid
    changed?: Dayjs
    changedBy?: Guid
    linkUpdate?: IApiLink
    linkLastUpdateUserEmail?: IApiLink
    linkGetAssetDocuments?: IApiLink
    linkAddAssetDocuments?: IApiLink
    linkGetAssetDocumentCategories?: IApiLink
    linkGetAssetCollection?: IApiLink
    linkUpdateAssetCollection?: IApiLink
    linkGetAssetVersions?: IApiLink
    linkGetDisposedByUserEmail?: IApiLink
    linkGetAssetDisplayStatus?: IApiLink
    linkGetMonthlyRentalCosts?: IApiLink
    linkUpdateAltArticleDescription?: IApiLink
    linkGetAssetFinancialAndListPrice?: IApiLink
    linkGetAssetLabelGroups?: IApiLink
    linkCreateAssetMaintenance?: IApiLink
    linkGetOperationHours?: IApiLink
    linkAddOperationHours?: IApiLink
    eTag?: string
}

export interface IAssetLifeCycleInvalidFields {
    availabilityDate?: boolean
    disposalDate?: boolean
    disposalReason?: boolean
    expectedReturnDate?: boolean
}

export default class Asset implements IAsset {
    id?: Guid
    articleId: Guid
    articleDescription?: string
    altArticleDescription?: string
    ownershipType: AssetOwnershipType
    lmid: string
    constructionYear: number
    isSecondhand: boolean
    origin: AssetOrigin | null
    internalOrderNumber?: string | null
    supplierOrderNo?: string | null
    isOperational: boolean
    condition?: number
    holderName?: string
    purposes: Purpose[]
    drawingNo?: string | null
    inventoryNo?: string | null
    internalNotes?: string | null
    serialNo?: string | null
    serialNoMachine?: string | null
    manufacturerCode?: string | null
    operationHours?: number | null
    operationHoursTime?: Dayjs | null
    width?: number | null
    height?: number | null
    length?: number | null
    weight?: number | null
    volume?: number | null
    quickCouplingSystem?: string | null
    connectionType?: string | null
    teeth?: boolean | null
    cuttingEdge?: boolean | null
    heavyDuty?: boolean | null
    locking?: string | null
    controlSystem?: string | null
    agility?: string | null
    measurementSystemId?: Guid | null
    emissionLevel?: string | null
    availabilityDate?: Dayjs | null
    expectedReturnDate?: Dayjs | null
    availability?: AssetAvailability | null
    plannedUntilOrUnavailableFromDate?: Dayjs | null
    availabilityReservationNumber?: string | null
    availabilityReservationId?: Guid | null
    features?: ITranslations | null
    freightUnits: IGetFreightUnit[]
    splitParentId?: Guid | null
    splitParentLmid?: string | null
    splitDate?: Dayjs | null
    assetSplitIsRunning?: boolean
    isLabeled?: boolean
    labels: string[]
    assetLocation: AssetLocation
    buyBack?: IAssetBuyBack | null
    warehouseId?: Guid
    profitCenterId?: Guid
    profitCenterDescription?: string
    lastUpdateDate?: Dayjs
    assetFinancial: {
        referenceNumberSale: string
        referenceNumberRental?: string | null
        referenceValue?: AmountWithCurrency
        referenceValueCorrection?: number | null
        assetCategory?: AssetCategory
        assetType?: AssetType
        assetProduct?: AssetProduct
        monthlyRentalCost?: MonthlyRentalCost
        initialReferenceNumberSale?: string | null
        initialReferenceNumberRental?: string | null
    }
    disposal?: {
        reason: AssetDisposalReason
        date: Dayjs
        description?: string
        disposedByEmail?: string
    }
    linkedToAssetCollection?: boolean
    version?: number
    isCurrentVersion?: boolean
    owner?: Customer | null
    colorId?: Guid
    assetListPrice?: {
        listPrice?: number | null
        listPriceDate?: Dayjs | null
        discount?: number | null
        discountAmount?: number | null
        salesSupport?: number | null
        salesPrice?: number | null
        salesPriceAdjustedCondition?: number | null
    } | null
    assetStorage?: {
        title?: string
        description?: string
    } | null
    status: AssetStatus
    wasReleased?: boolean
    assetRequestStatistic?: IGetAssetRequestStatistic | null
    lastScanDate?: Dayjs
    created?: Dayjs
    createdBy?: Guid
    changed?: Dayjs
    changedBy?: Guid
    linkUpdate?: IApiLink
    linkGetAssetDocuments?: IApiLink
    linkAddAssetDocuments?: IApiLink
    linkGetAssetDocumentCategories?: IApiLink
    linkGetAssetCollection?: IApiLink
    linkUpdateAssetCollection?: IApiLink
    linkGetAssetVersions?: IApiLink
    linkGetDisposedByUserEmail?: IApiLink
    linkGetAssetDisplayStatus?: IApiLink
    linkGetMonthlyRentalCosts?: IApiLink
    linkUpdateAltArticleDescription?: IApiLink
    linkGetAssetFinancialAndListPrice?: IApiLink
    linkGetAssetLabelGroups?: IApiLink
    linkCreateAssetMaintenance?: IApiLink
    linkGetOperationHours?: IApiLink
    linkAddOperationHours?: IApiLink
    eTag?: string

    hasFinancialData: boolean

    constructor(dto: IAsset) {
        this.id = dto.id
        this.articleId = dto.articleId
        this.articleDescription = dto.articleDescription
        this.altArticleDescription = dto.altArticleDescription
        this.ownershipType = dto.ownershipType
        this.lmid = dto.lmid
        this.constructionYear = dto.constructionYear
        this.isSecondhand = dto.isSecondhand
        this.origin = dto.origin
        this.internalOrderNumber = dto.internalOrderNumber
        this.supplierOrderNo = dto.supplierOrderNo
        this.isOperational = dto.isOperational
        this.purposes = dto.purposes
        this.drawingNo = dto.drawingNo
        this.condition = dto.condition
        this.inventoryNo = dto.inventoryNo
        this.internalNotes = dto.internalNotes
        this.serialNo = dto.serialNo
        this.serialNoMachine = dto.serialNoMachine
        this.manufacturerCode = dto.manufacturerCode
        this.holderName = dto.holderName
        this.operationHours = dto.operationHours
        this.operationHoursTime = dto.operationHoursTime
        this.width = dto.width
        this.height = dto.height
        this.length = dto.length
        this.weight = dto.weight
        this.volume = dto.volume
        this.quickCouplingSystem = dto.quickCouplingSystem
        this.connectionType = dto.connectionType
        this.teeth = dto.teeth
        this.cuttingEdge = dto.cuttingEdge
        this.heavyDuty = dto.heavyDuty
        this.locking = dto.locking
        this.controlSystem = dto.controlSystem
        this.agility = dto.agility
        this.measurementSystemId = dto.measurementSystemId
        this.emissionLevel = dto.emissionLevel
        this.availabilityDate = dto.availabilityDate
        this.expectedReturnDate = dto.expectedReturnDate
        this.availability = dto.availability
        this.plannedUntilOrUnavailableFromDate = dto.plannedUntilOrUnavailableFromDate
        this.availabilityReservationNumber = dto.availabilityReservationNumber
        this.availabilityReservationId = dto.availabilityReservationId
        this.splitParentId = dto.splitParentId
        this.splitParentLmid = dto.splitParentLmid
        this.splitDate = dto.splitDate
        this.assetSplitIsRunning = dto.assetSplitIsRunning
        this.features = dto.features
        this.freightUnits = dto.freightUnits
        this.isLabeled = dto.isLabeled
        this.labels = dto.labels || []
        this.assetLocation = dto.assetLocation
        this.buyBack = dto.buyBack
        this.warehouseId = dto.warehouseId
        this.profitCenterId = dto.profitCenterId
        this.profitCenterDescription = dto.profitCenterDescription
        this.assetFinancial = dto.assetFinancial
        this.disposal = dto.disposal
        this.linkedToAssetCollection = dto.linkedToAssetCollection
        this.version = dto.version
        this.isCurrentVersion = dto.isCurrentVersion
        this.owner = dto.owner
        this.colorId = dto.colorId
        this.assetListPrice = dto.assetListPrice
        this.assetStorage = dto.assetStorage
        this.status = dto.status
        this.wasReleased = dto.wasReleased
        this.assetRequestStatistic = dto.assetRequestStatistic
        this.lastScanDate = dto.lastScanDate
        this.created = dto.created
        this.createdBy = dto.createdBy
        this.changed = dto.changed
        this.changedBy = dto.changedBy
        this.linkUpdate = dto.linkUpdate
        this.linkGetAssetDocuments = dto.linkGetAssetDocuments
        this.linkAddAssetDocuments = dto.linkAddAssetDocuments
        this.linkGetAssetDocumentCategories = dto.linkGetAssetDocumentCategories
        this.linkGetAssetCollection = dto.linkGetAssetCollection
        this.linkGetAssetVersions = dto.linkGetAssetVersions
        this.linkGetDisposedByUserEmail = dto.linkGetDisposedByUserEmail
        this.linkGetAssetDisplayStatus = dto.linkGetAssetDisplayStatus
        this.linkGetMonthlyRentalCosts = dto.linkGetMonthlyRentalCosts
        this.linkUpdateAssetCollection = dto.linkUpdateAssetCollection
        this.linkUpdateAltArticleDescription = dto.linkUpdateAltArticleDescription
        this.linkGetAssetFinancialAndListPrice = dto.linkGetAssetFinancialAndListPrice
        this.linkGetAssetLabelGroups = dto.linkGetAssetLabelGroups
        this.linkCreateAssetMaintenance = dto.linkCreateAssetMaintenance
        this.linkGetOperationHours = dto.linkGetOperationHours
        this.linkAddOperationHours = dto.linkAddOperationHours
        this.eTag = dto.eTag
        this.hasFinancialData = !!Object.values(dto.assetFinancial).some(v => v) || !!dto.assetListPrice
    }

    static fromDto(dto: IGetAssetDto, additionalData?: {
        disposedByEmail?: string
        assetFinancial?: IGetAssetFinancialDto
        assetListPrice?: IGetAssetListPriceDto
    }): Asset {
        return new Asset({
            id: dto.id,
            articleId: dto.articleId,
            altArticleDescription: dto.altArticleDescription ?? undefined,
            ownershipType: dto.ownershipType,
            lmid: dto.lmid,
            constructionYear: dto.constructionYear,
            isSecondhand: dto.isSecondhand,
            origin: dto.origin,
            internalOrderNumber: dto.internalOrderNumber,
            supplierOrderNo: dto.supplierOrderNo,
            isOperational: dto.isOperational,
            condition: typeof dto.condition === "number" ? dto.condition : undefined,
            purposes: dto.purposes,
            drawingNo: dto.drawingNo,
            inventoryNo: dto.inventoryNo,
            internalNotes: dto.internalNotes,
            serialNo: dto.serialNumber,
            serialNoMachine: dto.serialNumberMachine,
            manufacturerCode: dto.manufacturerCode,
            holderName: dto.holderName ?? undefined,
            operationHours: dto.operationHours,
            operationHoursTime: dto.operationHoursTime ? dayjs(dto.operationHoursTime) : undefined,
            width: dto.width,
            height: dto.height,
            length: dto.length,
            weight: dto.weight,
            volume: dto.volume,
            quickCouplingSystem: dto.quickCouplingSystem,
            connectionType: dto.connectionType,
            teeth: dto.teeth,
            cuttingEdge: dto.cuttingEdge,
            heavyDuty: dto.heavyDuty,
            locking: dto.locking,
            controlSystem: dto.controlSystem,
            agility: dto.agility,
            measurementSystemId: dto.measurementSystemId,
            emissionLevel: dto.emissionLevel,
            availabilityDate: dto.availabilityDate ? dayjs.utc(dto.availabilityDate) : undefined,
            expectedReturnDate: dto.expectedReturnDate ? dayjs.utc(dto.expectedReturnDate) : undefined,
            availability: dto.availability,
            plannedUntilOrUnavailableFromDate: dto.plannedUntilOrUnavailableFromDate ? dayjs.utc(dto.plannedUntilOrUnavailableFromDate) : undefined,
            availabilityReservationNumber: dto.availabilityReservationNumber,
            availabilityReservationId: dto.availabilityReservationId,
            splitParentId: dto.splitParentId,
            splitParentLmid: dto.splitParentLmid,
            splitDate: dto.splitDate ? dayjs.utc(dto.splitDate) : null,
            assetSplitIsRunning: dto.assetSplitIsRunning,
            features: dto.features,
            freightUnits: dto.freightUnits || [],
            isLabeled: dto.isLabeled,
            labels: dto.labels,
            assetLocation: new AssetLocation(dto.assetLocation),
            buyBack: dto.buyBack,
            warehouseId: dto.warehouseId ?? undefined,
            profitCenterId: dto.profitCenterId ?? undefined,
            profitCenterDescription: dto.profitCenterDescription ?? undefined,
            assetFinancial: {
                referenceNumberSale: additionalData?.assetFinancial?.referenceNumberSale ?? "",
                referenceNumberRental: additionalData?.assetFinancial?.referenceNumberRental,
                referenceValue: additionalData?.assetFinancial?.referenceValue ? new AmountWithCurrency(additionalData?.assetFinancial?.referenceValue) : undefined,
                referenceValueCorrection: additionalData?.assetFinancial?.referenceValueCorrection ?? undefined,
                assetCategory: additionalData?.assetFinancial?.assetCategory ? new AssetCategory(additionalData?.assetFinancial?.assetCategory) : undefined,
                assetType: additionalData?.assetFinancial?.assetType ? new AssetType(additionalData?.assetFinancial?.assetType) : undefined,
                assetProduct: additionalData?.assetFinancial?.assetProduct ? new AssetProduct(additionalData?.assetFinancial?.assetProduct) : undefined,
                monthlyRentalCost: additionalData?.assetFinancial?.monthlyRentalCost ? new MonthlyRentalCost(additionalData?.assetFinancial?.monthlyRentalCost) : undefined,
                initialReferenceNumberSale: additionalData?.assetFinancial?.initialReferenceNumberSale,
                initialReferenceNumberRental: additionalData?.assetFinancial?.initialReferenceNumberRental
            },
            disposal: dto.disposal ? {
                reason: dto.disposal.reason,
                date: dayjs.utc(dto.disposal.date),
                description: dto.disposal.description || undefined,
                disposedByEmail: additionalData?.disposedByEmail || undefined,
            } : undefined,
            linkedToAssetCollection: dto.linkedToAssetCollection,
            version: dto.version,
            isCurrentVersion: dto.isCurrentVersion,
            owner: dto.owner ? new Customer(dto.owner) : undefined,
            colorId: dto.colorId ?? undefined,
            assetListPrice: additionalData?.assetListPrice ? {
                ...additionalData?.assetListPrice,
                listPriceDate: additionalData?.assetListPrice.listPriceDate ? dayjs(additionalData?.assetListPrice.listPriceDate, "YYYY-MM-DD") : null,
            } : null,
            assetRequestStatistic: dto.assetRequestStatistic ? {
                ...dto.assetRequestStatistic,
                changeDate: dto.assetRequestStatistic.changeDate ? dayjs(dto.assetRequestStatistic.changeDate) : null,
            } : null,
            assetStorage: dto.assetStorage?.title || dto.assetStorage?.description ? {
                title: dto.assetStorage.title ?? undefined,
                description: dto.assetStorage.description ?? undefined,
            } : null,
            status: dto.status,
            wasReleased: dto.wasReleased,
            lastScanDate: dto.lastScanDate ? dayjs(dto.lastScanDate) : undefined,
            created: dto.created ? dayjs(dto.created) : undefined,
            createdBy: dto.createdBy,
            changed: dto.changed ? dayjs(dto.changed) : undefined,
            changedBy: dto.changedBy,
            linkUpdate: dto.links.find(lnk => lnk.entity === ApiLinkEntity.asset && lnk.rel === ApiLinkRel.partialUpdate),
            linkGetAssetDocuments: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetDocument && lnk.rel === ApiLinkRel.all),
            linkAddAssetDocuments: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetDocument && lnk.rel === ApiLinkRel.new),
            linkGetAssetDocumentCategories: dto.links.find(lnk => lnk.entity === ApiLinkEntity.documentCategory && lnk.rel === ApiLinkRel.all),
            linkGetAssetCollection: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetCollection && lnk.rel === ApiLinkRel.self),
            linkUpdateAssetCollection: dto.links?.find(lnk => lnk.entity === ApiLinkEntity.assetCollection && lnk.rel === ApiLinkRel.bulkUpdate),
            linkGetAssetVersions: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetVersion && lnk.rel === ApiLinkRel.all),
            linkGetAssetDisplayStatus: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetDisplayStatus && lnk.rel === ApiLinkRel.all),
            linkGetMonthlyRentalCosts: dto.links.find(lnk => lnk.entity === ApiLinkEntity.monthlyRentalCost && lnk.rel === ApiLinkRel.all),
            linkUpdateAltArticleDescription: dto.links.find(lnk => lnk.entity === ApiLinkEntity.altArticleDescription && lnk.rel === ApiLinkRel.update),
            linkGetAssetFinancialAndListPrice: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetFinancialAndListPrice && lnk.rel === ApiLinkRel.self),
            linkGetAssetLabelGroups: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetLabels && lnk.rel === ApiLinkRel.all),
            linkCreateAssetMaintenance: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetMaintenance && lnk.rel === ApiLinkRel.new),
            linkGetOperationHours: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetOperationHours && lnk.rel === ApiLinkRel.all),
            linkAddOperationHours: dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetOperationHours && lnk.rel === ApiLinkRel.new),
            eTag: dto.eTag,
        })
    }

    toDto({ noLmid }: { noLmid?: boolean } = {}): IPostAssetDto {
        return {
            ownershipType: this.ownershipType,
            articleId: this.articleId,
            lmid: noLmid ? undefined : this.lmid,
            constructionYear: this.constructionYear,
            isSecondhand: this.isSecondhand,
            origin: this.origin,
            internalOrderNumber: this.internalOrderNumber,
            supplierOrderNo: this.supplierOrderNo,
            isOperational: this.isOperational,
            condition: typeof this.condition === "number" ? this.condition : undefined,
            purposes: this.purposes,
            drawingNo: this.drawingNo ?? undefined,
            inventoryNo: this.inventoryNo,
            internalNotes: this.internalNotes,
            serialNumber: this.serialNo ?? undefined,
            serialNumberMachine: this.serialNoMachine ?? undefined,
            manufacturerCode: this.manufacturerCode || "",
            operationHours: typeof this.operationHours === "number" ? this.operationHours : undefined,
            operationHoursTime: this.operationHoursTime?.toISOString(),
            width: this.width,
            height: this.height,
            length: this.length,
            weight: this.weight,
            volume: this.volume,
            quickCouplingSystem: this.quickCouplingSystem,
            connectionType: this.connectionType,
            teeth: this.teeth,
            cuttingEdge: this.cuttingEdge,
            heavyDuty: this.heavyDuty,
            locking: this.locking,
            controlSystem: this.controlSystem,
            agility: this.agility,
            measurementSystemId: this.measurementSystemId || null,
            emissionLevel: this.emissionLevel,
            availabilityDate: this.availabilityDate?.isValid() ? toDateTimeWithoutTimezone(this.availabilityDate) : undefined,
            expectedReturnDate: this.expectedReturnDate?.isValid() ? toDateTimeWithoutTimezone(this.expectedReturnDate) : undefined,
            features: this.features ? prepareTranslations(this.features) : null,
            freightUnits: this.freightUnits,
            assetLocation: this.assetLocation.toDto(),
            buyBack: this.buyBack ?? null,
            warehouseId: this.warehouseId ?? null,
            profitCenterId: this.profitCenterId ?? null,
            assetFinancial: {
                referenceNumberSale: this.assetFinancial.referenceNumberSale,
                referenceNumberRental: this.assetFinancial.referenceNumberRental,
                referenceValue: this.assetFinancial.referenceValue ? new AmountWithCurrency(this.assetFinancial.referenceValue) : undefined,
                referenceValueCorrection: this.assetFinancial.referenceValueCorrection ?? null,
                assetCategoryId: this.assetFinancial.assetCategory?.id,
                assetTypeId: this.assetFinancial.assetType?.id,
                assetProductId: this.assetFinancial.assetProduct?.id,
                monthlyRentalCost: this.assetFinancial.monthlyRentalCost?.toDto(),
                initialReferenceNumberSale: this.assetFinancial.initialReferenceNumberSale,
                initialReferenceNumberRental: this.assetFinancial.initialReferenceNumberRental
            },
            disposal: this.disposal ? {
                reason: this.disposal.reason,
                date: toDateTimeWithoutTimezone(this.disposal.date),
                description: this.disposal.description || undefined,
            } : undefined,
            owner: this.owner?.toDto(),
            colorId: this.colorId ?? null,
            assetListPrice: this.assetListPrice ? {
                ...this.assetListPrice,
                listPriceDate: this.assetListPrice.listPriceDate ? toDateWithoutTimezone(this.assetListPrice.listPriceDate) : null,
            } : undefined,
            assetRequestStatistic: this.assetRequestStatistic && !this.assetRequestStatistic.locked ? {
                relevantForAssetStatistic: this.assetRequestStatistic.relevantForAssetStatistic,
                reportAsset: this.assetRequestStatistic.reportAsset,
                reservationId: this.assetRequestStatistic.reservationId || null,
            } : undefined,
            status: this.disposal ? undefined : this.status,
        }
    }

    get hasMultipleVersions(): boolean {
        return !!this.linkGetAssetVersions
    }

    get isPurchased(): boolean {
        return this.ownershipType === AssetOwnershipType.purchased
    }

    get isBorrowed(): boolean {
        return this.ownershipType === AssetOwnershipType.borrowed
    }

    get hasFeaturesOrFreightUnits(): boolean {
        return this.freightUnits.length > 0
            || (!!this.features && Object.values(this.features).some(t => !!t))
    }

    getProductNumberParts(withoutProductCode?: boolean): (string | undefined)[] {
        const parts = [
            this.assetFinancial?.assetCategory?.name,
            this.assetFinancial?.assetType?.productType.toString(),
        ]
        if (!withoutProductCode) parts.push(
            this.assetFinancial?.assetProduct?.code)
        return parts
    }

    getProductGroupNumber(withoutProductCode?: boolean): string {
        const parts = this.getProductNumberParts(withoutProductCode)
        const isNumberIncomplete = parts.some(p => !p)
        if (isNumberIncomplete) return ""
        return parts.join("")
    }
}

export const getAssetStatusLabel = (status: AssetStatus): string => {
    return {
        [AssetStatus.ordered]: i18n.t('assetStatus.ordered'),
        [AssetStatus.confirmed]: i18n.t('assetStatus.confirmed'),
        [AssetStatus.available]: i18n.t('assetStatus.available'),
        [AssetStatus.active]: i18n.t('assetStatus.active'),
        [AssetStatus.inactive]: i18n.t('assetStatus.inactive'),
    }[status] || status
}
